import React, { useState } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';
import LockPasswordIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';
import { useHistory, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import cs from 'classnames';

import { getMyUserInfo, loginWithEmailAndPassword, selfRegister } from '../../../../api/usersApi';
import { ContainedButton } from '../../../../components/modules/Buttons';
import { NOTIFICATION, PATHS, RolesLowerCase } from '../../../../enums';
import {
  bootstrapComposerWebPreferences,
  isSafari,
  setTokenLocalStorage,
} from '../../../../helpers';
import { saveUser } from '../../../../store/user/userSlice';
import CrossIcon from '../../../../assets/icons/cross.svg';
import Modal from '../../../../components/modules/Modal';
import { useAppDispatch } from '../../../../store/hooks';
import styles from './SelfRegister.module.scss';
import { Tooltip } from '@mui/material';

type TRegisterModal = {
  open: boolean;
  switchModal: () => void;
};

const safari = isSafari();

const RegisterModal: React.FC<TRegisterModal> = ({ open, switchModal }) => {
  const dispatch = useAppDispatch();
  const history = useHistory();

  const notification = useSnackbar();
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const [creds, setCreds] = useState({
    password: '',
    password_repeat: '',
  });
  const searchParams = new URLSearchParams(location.search);
  const registerValue = searchParams.get('invitation_token');
  const username = searchParams.get('username') || '';
  const email = searchParams.get('email') || '';

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCreds({
      ...creds,
      [e.target.name]: e.target.value,
    });
  };

  const selfRegistration = async (data: { password: string; password_repeat: string }) => {
    setLoading(true);

    try {
      const registeredUser = await selfRegister(data, registerValue);
      if (registeredUser.status === 200) {
        const res = await loginWithEmailAndPassword({ email: registeredUser?.data?.email, password: data.password });

        if (!res?.data?.accessToken && !res?.data?.refreshToken && res?.data?.organizations.length && res?.data?.message) {
          notification.showMessage(res.data.message);
          const logRes = await loginWithEmailAndPassword({
            email: registeredUser?.data?.email,
            password: data.password,
            selectedOrgId: res.data.organizations[0].id,
          });
          if (logRes) {
            setTokenLocalStorage(logRes.data.accessToken);
          }
        } else {
          setTokenLocalStorage(res.data.accessToken);
        }
        const user = await getMyUserInfo();
        await bootstrapComposerWebPreferences();
        dispatch(saveUser(user.data));
        notification.showMessage(NOTIFICATION.LOGGED_IN);
        switchModal();
        if (user?.data?.role?.title === RolesLowerCase.VIEWER) {
          history.push(`${PATHS.SHARED_WITH_ME.path}`);
        } else if (user?.data?.role?.title === RolesLowerCase.SUPER_ADMIN) {
          history.push(`${PATHS.ADMIN_PAGE.path}`);
        } else {
          history.push(`${PATHS.MY_FOLDERS.path}`);
        }
      }

      switchModal();
    } catch (err) {
      // @ts-ignore
      if (err?.response?.data?.message) {
        // @ts-ignore
        notification.showMessage(err.response.data.message);
      } else {
        notification.showMessage('Wrong credentials');
      }
    } finally {
      setLoading(false);
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      password: creds.password,
      password_repeat: creds.password_repeat,
    },
  });

  const [type, setType] = useState<'password' | 'text'>('password');
  const [repeatPasswordType, setRepeatPasswordType] = useState<'password' | 'text'>('password');

  const switchType = () => {
    if (type === 'password') {
      setType('text');
    } else {
      setType('password');
    }
  };

  const switchRepeatPasswordType = () => {
    if (repeatPasswordType === 'password') {
      setRepeatPasswordType('text');
    } else {
      setRepeatPasswordType('password');
    }
  };

  return (
    <Modal open={open} onClose={switchModal} loading={loading}>
      <div className={styles.modalBody}>
        <div className={styles.modalHeader}>
          <div className={styles.modalTitle}>Complete Registration</div>
          <img src={CrossIcon} alt="close modal" onClick={switchModal} className={styles.closeIcon} />
        </div>
        <form
          className={styles.form}
          onSubmit={handleSubmit((data) => {
            selfRegistration(data);
          })}
        >
          <input value={username} className={styles.field} name="username" type="text" disabled />
          <input value={email} className={styles.field} name="email" type="text" disabled />
          <div style={{ position: 'relative' }}>
            <input
              {...register('password', {
                required: true,
                minLength: {
                  value: 8,
                  message: 'Password must contain at least 8 characters',
                },
                validate: (value) => value === creds.password_repeat || '',
              })}
              value={creds.password}
              className={errors.password ? styles.error : styles.field}
              onChange={onChange}
              placeholder="Password"
              name="password"
              type={type}
              style={{ color: 'white' }}
              required
            />
            {!safari ? (
              <span onClick={switchType} className={styles.passwordIcon}>
                {' '}
                {type === 'password' ? 'Show' : 'Hide'}{' '}
              </span>
            ) : type !== 'password' ? (
              <LockPasswordIcon onClick={switchType} style={{ position: 'absolute', right: '-21px', top: '8px', fontSize: '20px' }} />
            ) : (
              <LockIcon onClick={switchType} style={{ position: 'absolute', right: '-21px', top: '8px', fontSize: '20px' }} />
            )}
          </div>
          <div style={{ position: 'relative' }}>
            <input
              {...register('password_repeat', {
                required: true,
                minLength: {
                  value: 8,
                  message: 'Password must contain at least 8 characters',
                },
                validate: (value) => value === creds.password || 'Passwords do not match.',
              })}
              value={creds.password_repeat}
              className={errors.password_repeat ? styles.error : styles.field}
              onChange={onChange}
              placeholder="Repeat password"
              name="password_repeat"
              type={repeatPasswordType}
              required
            />
            {!safari ? (
              <span onClick={switchRepeatPasswordType} className={styles.passwordIcon}>
                {' '}
                {repeatPasswordType === 'password' ? 'Show' : 'Hide'}{' '}
              </span>
            ) : repeatPasswordType !== 'password' ? (
              <LockPasswordIcon
                onClick={switchRepeatPasswordType}
                style={{ position: 'absolute', right: '-21px', top: '8px', fontSize: '20px' }}
              />
            ) : (
              <LockIcon onClick={switchRepeatPasswordType} style={{ position: 'absolute', right: '-21px', top: '8px', fontSize: '20px' }} />
            )}
          </div>
          <div className={errors.password?.message ? cs(styles.message, styles.red) : styles.message}>
            Password requires at least 8 characters
          </div>
          <div className={styles.actionBtns}>
            <ContainedButton disabled={loading} type="submit">
              Register
            </ContainedButton>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default RegisterModal;
